import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { SyncStatusBar } from '../components/offline/SyncStatusBar'
import { useNetworkStatus } from '../hooks/useOfflineSync'

interface NavItem {
  path: string
  label: string
  icon: string
  roles?: string[]
}

const navItems: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: '📊' },
  { path: '/galeria', label: 'Galeria de Fotos', icon: '📸' },
  { path: '/atiradores', label: 'Meu Cadastro', icon: '🎯', roles: ['atirador'] },
  { path: '/minhas-armas', label: 'Minhas Armas', icon: '🔫', roles: ['atirador'] },
  { path: '/inscricoes', label: 'Inscricoes', icon: '📝', roles: ['atirador'] },
  { path: '/minhas-inscricoes', label: 'Minhas Inscricoes', icon: '📝', roles: ['atirador'] },
  { path: '/rankings', label: 'Rankings', icon: '🏆', roles: ['atirador'] },
  { path: '/relatorios', label: 'Relatorios', icon: '📄', roles: ['atirador'] },
  { path: '/declaracoes', label: 'Declaracoes', icon: '📜', roles: ['atirador'] },
  { path: '/habitualidade', label: 'Habitualidade', icon: '📅', roles: ['atirador'] },
  // Itens abaixo sao para os perfis "de staff" (nao-atirador). As roles em cada
  // item refletem exatamente as permissoes concedidas em database/seed.ts
  // (ROLE_PERMISSOES) — gestor_campeonato, operador_inscricao e operador_resultado
  // antes cai am no filtro generico (so viam Dashboard/Galeria/Fila Offline).
  { path: '/clubes', label: 'Clubes', icon: '🏢', roles: ['admin', 'gestor_campeonato', 'operador_inscricao', 'operador_resultado', 'consulta'] },
  { path: '/atiradores', label: 'Atiradores', icon: '🎯', roles: ['admin', 'gestor_campeonato', 'operador_inscricao', 'operador_resultado', 'consulta'] },
  { path: '/convites', label: 'Convites de Inscricao', icon: '✉️', roles: ['admin', 'gestor_campeonato', 'operador_inscricao'] },
  { path: '/armas', label: 'Armas', icon: '🔫', roles: ['admin', 'gestor_campeonato', 'operador_inscricao'] },
  { path: '/campeonatos', label: 'Campeonatos', icon: '🏆', roles: ['admin', 'gestor_campeonato', 'operador_inscricao', 'operador_resultado', 'consulta'] },
  { path: '/modalidades', label: 'Modalidades', icon: '📋', roles: ['admin', 'gestor_campeonato', 'operador_inscricao', 'operador_resultado', 'consulta'] },
  { path: '/etapas', label: 'Etapas', icon: '📅', roles: ['admin', 'gestor_campeonato', 'operador_inscricao', 'operador_resultado', 'consulta'] },
  { path: '/inscricoes', label: 'Inscricoes', icon: '📝', roles: ['admin', 'gestor_campeonato', 'operador_inscricao', 'operador_resultado', 'consulta'] },
  { path: '/inscricoes/consulta', label: 'Consulta de Inscricoes', icon: '🔎', roles: ['admin', 'gestor_campeonato', 'operador_inscricao', 'operador_resultado', 'consulta'] },
  { path: '/inscricoes/pendentes', label: 'Provas Pendentes', icon: '⏳', roles: ['admin', 'gestor_campeonato', 'operador_inscricao', 'operador_resultado', 'consulta'] },
  { path: '/resultados', label: 'Resultados', icon: '🎯', roles: ['admin', 'operador_resultado', 'consulta'] },
  // 016_: IPSC - pistas, squads, lancamento por pista e apuracao por divisao
  { path: '/ipsc', label: 'IPSC (pistas e squads)', icon: '🎯', roles: ['admin', 'gestor_campeonato', 'operador_resultado', 'consulta'] },
  { path: '/rankings', label: 'Rankings', icon: '🏆', roles: ['admin', 'gestor_campeonato', 'operador_inscricao', 'operador_resultado', 'consulta'] },
  { path: '/relatorios', label: 'Relatorios', icon: '📄', roles: ['admin', 'gestor_campeonato', 'operador_resultado'] },
  { path: '/declaracoes', label: 'Declaracoes', icon: '📜', roles: ['admin'] },
  { path: '/habitualidade', label: 'Habitualidade', icon: '📅', roles: ['admin'] },
  { path: '/financeiro', label: 'Financeiro', icon: '💰', roles: ['admin'] },
  { path: '/acesso', label: 'Controle de Acesso', icon: '🔐', roles: ['admin'] },
  { path: '/offline-queue', label: 'Fila Offline', icon: '📤' },
]

export function AppLayout() {
  const { usuario, logout, hasRole } = useAuth()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const isOnline = useNetworkStatus()

  // Um item sem `roles` fica visivel pra qualquer usuario autenticado; com
  // `roles`, basta o usuario ter UMA das roles listadas (nao so admin/atirador
  // como antes — isso deixava gestor_campeonato/operador_inscricao/
  // operador_resultado/consulta sem nenhum item de menu util).
  const visibleItems = navItems.filter((item) => {
    if (!item.roles) return true
    return item.roles.some(role => hasRole(role))
  })

  async function handleLogout() {
    await logout()
    navigate('/login')
  }

  function handleNavClick() {
    setSidebarOpen(false)
  }

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="px-6 py-5 border-b border-compumaq-dourado/25 bg-compumaq-preto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/compumaq-logo.jpg" alt="Compumaq Tecnologia" className="h-10 w-10 rounded-lg object-cover" />
          <div>
            <h1 className="text-lg font-bold text-compumaq-texto">Tiro Esportivo</h1>
            <p className="text-xs text-compumaq-textoSuave mt-0.5">Campeonatos</p>
          </div>
        </div>
        {/* Botao fechar no mobile */}
        <button
          className="md:hidden p-1 rounded-lg text-compumaq-textoSuave hover:bg-compumaq-pretoSuave"
          onClick={() => setSidebarOpen(false)}
          aria-label="Fechar menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <ul className="space-y-0.5">
          {visibleItems.map((item, idx) => (
            <li key={`${item.path}-${idx}`}>
              <NavLink
                to={item.path}
                end={item.path === '/dashboard' || item.path === '/inscricoes'}
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? 'bg-compumaq-dourado/15 text-compumaq-doradoClaro font-medium'
                      : 'text-compumaq-textoSuave hover:bg-compumaq-preto hover:text-compumaq-texto'
                  }`
                }
              >
                <span className="text-base">{item.icon}</span>
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* User info */}
      <div className="px-4 py-4 border-t border-compumaq-dourado/25">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-compumaq-dourado/15 flex items-center justify-center text-compumaq-doradoClaro font-semibold text-sm">
            {usuario?.nome?.charAt(0)?.toUpperCase() ?? 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-compumaq-texto truncate">{usuario?.nome ?? 'Usuario'}</p>
            <p className="text-xs text-compumaq-textoSuave truncate">{usuario?.email ?? ''}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          Sair
        </button>
      </div>
    </>
  )

  return (
    <div className="flex h-screen bg-compumaq-preto">
      {/* Overlay mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar desktop */}
      <aside className="hidden md:flex w-60 bg-compumaq-pretoSuave border-r border-compumaq-dourado/25 flex-col shadow-sm">
        <SidebarContent />
      </aside>

      {/* Sidebar mobile (drawer) */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-72 bg-compumaq-pretoSuave border-r border-compumaq-dourado/25 flex flex-col shadow-xl transition-transform duration-300 md:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent />
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar mobile */}
        <header className="md:hidden bg-compumaq-preto border-b border-compumaq-dourado/25 px-4 py-3 flex items-center gap-3 shadow-sm">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg text-compumaq-textoSuave hover:bg-compumaq-pretoSuave"
            aria-label="Abrir menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <img src="/compumaq-logo.jpg" alt="Compumaq Tecnologia" className="h-8 w-8 rounded object-cover" />
          <div className="flex-1">
            <h1 className="text-base font-bold text-compumaq-texto">Tiro Esportivo</h1>
          </div>
          <span
            className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              isOnline ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}
            title={isOnline ? 'Online' : 'Offline'}
          >
            {isOnline ? '📶' : '📴'}
          </span>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
            <Outlet />
          </div>
        </main>
      </div>
      <SyncStatusBar />
    </div>
  )
}
